/**
 * Web→RN localStorage shim.
 *
 * The garageiq-web codebase uses `localStorage.getItem/setItem` extensively
 * (VehicleSelector, GarageTabs, GarageClient, DiagnoseClient, StepUserContent,
 * ShopScheduler). React Native has no synchronous storage; AsyncStorage is the
 * standard, but switching every call site to async would be a large diff.
 *
 * This module provides:
 *  - `storage.getItem/setItem/removeItem` — async API (the source of truth)
 *  - `usePersistentState(key, initial)` — drop-in replacement for the common
 *    `const [v, setV] = useState(JSON.parse(localStorage.getItem(key)) ?? init)`
 *    pattern, with an `isHydrated` flag so screens can render skeleton UI
 *    while the value loads.
 *
 * For ported components, prefer `usePersistentState` — it preserves the
 * read-write ergonomics of localStorage without lying about sync timing.
 */
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useEffect, useState } from 'react';

export const storage = {
  async getItem(key) {
    try {
      const raw = await AsyncStorage.getItem(key);
      return raw == null ? null : JSON.parse(raw);
    } catch (err) {
      // Corrupt JSON → wipe the key so the app self-heals next launch.
      console.warn(`[storage] getItem("${key}") failed:`, err);
      try { await AsyncStorage.removeItem(key); } catch {}
      return null;
    }
  },
  async setItem(key, value) {
    try {
      await AsyncStorage.setItem(key, JSON.stringify(value));
    } catch (err) {
      console.warn(`[storage] setItem("${key}") failed:`, err);
    }
  },
  async removeItem(key) {
    try { await AsyncStorage.removeItem(key); } catch {}
  },
};

/**
 * useState backed by AsyncStorage. While the initial value is loading,
 * `isHydrated` is false — render a skeleton or just the initial value during
 * that frame to avoid flicker. After hydration, every setValue persists.
 *
 * @template T
 * @param {string} key
 * @param {T} initialValue
 * @returns {[T, (next: T | ((prev: T) => T)) => void, boolean]}
 */
export function usePersistentState(key, initialValue) {
  const [value, setValue] = useState(initialValue);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    let cancelled = false;
    storage.getItem(key).then(stored => {
      if (cancelled) return;
      if (stored !== null && stored !== undefined) setValue(stored);
      setIsHydrated(true);
    });
    return () => { cancelled = true; };
  }, [key]);

  // Persist on every change after hydration. Skip the very first render so we
  // don't overwrite a stored value with the initial default.
  useEffect(() => {
    if (!isHydrated) return;
    storage.setItem(key, value);
  }, [key, value, isHydrated]);

  return [value, setValue, isHydrated];
}
